// src/components/PipelinePicker.tsx
// Reusable searchable picker for selecting a workflow Pipeline. Task admin
// surfaces (type/policy/category/container defaults, transition rules) use
// this instead of raw pipeline-UUID inputs to wire task lifecycle events to
// the platform's single flow engine (see docs/design/workspace-redesign/08-workflow-integration.md).
// Only ACTIVE pipelines are offered — draft/paused/archived pipelines can't
// run, so surfacing them as pickable defaults would silently break automation.

import { useState, useEffect, useRef, useMemo } from 'react';
import { useWorkflow, useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import { formatEnumLabel } from '@/features/task/logic';
import type { Pipeline } from '@core/types/workflow';
import { AnchoredMenu } from './AnchoredMenu';

export interface PipelinePickerProps {
  /** Selected pipeline's entity id, or '' for none. */
  value: string;
  onChange: (pipelineId: string, pipeline?: Pipeline) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
}

export function PipelinePicker({
  value,
  onChange,
  label,
  placeholder,
  error,
  className = '',
  disabled,
}: PipelinePickerProps) {
  const store = useWorkflow();
  const { t } = useI18n();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loaded, setLoaded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Lazy-load active pipelines the first time the picker opens, then cache
  // for the lifetime of the component (list is small — no server search).
  useEffect(() => {
    if (!isOpen || loaded) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    store.ListPipelines()
      .then((res) => {
        if (cancelled) return;
        const items = (res?.items?.items || []).filter(
          (p) => p.status === 'PIPELINE_STATUS_ACTIVE',
        );
        setPipelines(items);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, loaded, store]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setSearch('');
    }
  }, [isOpen]);


  // Outside click, Escape and repositioning live in AnchoredMenu.

  const filtered = useMemo(() => {
    if (!search.trim()) return pipelines;
    const q = search.toLowerCase();
    return pipelines.filter((p) =>
      (p.name || '').toLowerCase().includes(q) || (p.slug || '').toLowerCase().includes(q),
    );
  }, [pipelines, search]);

  const selected = useMemo(
    () => (value ? pipelines.find((p) => p.entity?.id === value) : undefined),
    [value, pipelines],
  );

  const handleSelect = (pipeline: Pipeline) => {
    onChange(pipeline.entity?.id || '', pipeline);
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', undefined);
  };

  const triggerLabel = (p: Pipeline) => formatEnumLabel(String(p.trigger_type || ''), 'TRIGGER_TYPE_');

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
      )}

      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`
          w-full flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors
          ${error ? 'border-destructive focus:ring-destructive' : 'border-input focus:ring-ring'}
          ${isOpen ? 'ring-2 ring-ring' : ''}
          bg-card text-foreground hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        <svg
          className="h-4 w-4 text-muted-foreground flex-shrink-0"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
          strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
        </svg>

        {selected ? (
          <>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate leading-tight">{selected.name}</p>
            </div>
            <Badge variant="secondary" size="sm" className="flex-shrink-0">{triggerLabel(selected)}</Badge>
            <svg
              onClick={handleClear}
              className="h-4 w-4 text-muted-foreground hover:text-foreground flex-shrink-0 cursor-pointer"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </>
        ) : (
          <span className="flex-1 text-muted-foreground">
            {placeholder || t('task.admin.pipelinePicker.placeholder')}
          </span>
        )}

        <svg
          className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
          strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}

      <AnchoredMenu
        anchor={buttonRef}
        ignoreRef={containerRef}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        role="none"
        matchTriggerWidth
        minWidth={280}
        maxHeight={320}
        className="flex flex-col overflow-hidden"
      >
        <div className="p-2 border-b border-border flex-shrink-0">
          <div className="relative">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              placeholder={t('task.admin.pipelinePicker.searchPlaceholder')}
            />
          </div>
        </div>

        <div className="overflow-y-auto py-1" style={{ maxHeight: 'calc(100% - 46px)' }}>
          {loading ? (
            <div className="px-3 py-4 text-center">
              <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <p className="text-xs text-muted-foreground mt-1">{t('task.admin.pipelinePicker.loading')}</p>
            </div>
          ) : loadError ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-destructive">{t('task.admin.pipelinePicker.loadError')}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-3 py-4 text-center">
              <p className="text-sm text-muted-foreground">
                {search ? t('common.noResults') : t('task.admin.pipelinePicker.noPipelines')}
              </p>
            </div>
          ) : (
            <>
              {value && (
                <button
                  type="button"
                  onClick={() => { onChange('', undefined); setIsOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition-colors"
                >
                  <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                  <span className="text-sm text-muted-foreground">{t('task.admin.pipelinePicker.clear')}</span>
                </button>
              )}

              {filtered.map((p) => {
                const pId = p.entity?.id || '';
                const isSelected = pId === value;
                return (
                  <button
                    key={pId}
                    type="button"
                    onClick={() => handleSelect(p)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-muted'}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-foreground truncate">{p.name}</span>
                        {isSelected && (
                          <svg className="h-4 w-4 text-primary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        )}
                      </div>
                      {p.trigger_event && (
                        <p className="text-xs text-muted-foreground truncate font-mono">{p.trigger_event}</p>
                      )}
                    </div>
                    <Badge variant="secondary" size="sm" className="flex-shrink-0">{triggerLabel(p)}</Badge>
                  </button>
                );
              })}
            </>
          )}
        </div>
      </AnchoredMenu>
    </div>
  );
}

export default PipelinePicker;
